// Statistical watermark disruption.
// Sampling watermarks (Claude / GPT class) bias generation towards a "green
// list" of tokens. There is no key to remove, so the only local counter-measure
// is to lower the density of those favoured tokens while preserving meaning.
// Everything is deterministic: same input + same intensity = same output.

const RAW_EN = {
  // connectors & openers
  furthermore: ["also", "on top of that", "and"],
  moreover: ["also", "besides that", "and"],
  additionally: ["also", "on top of that", "plus"],
  however: ["but", "though", "still"],
  nevertheless: ["still", "even so", "yet"],
  nonetheless: ["still", "even so", "yet"],
  therefore: ["so", "which means", "as a result"],
  thus: ["so", "which means", "as a result"],
  hence: ["so", "which is why", "as a result"],
  consequently: ["so", "as a result", "which means"],
  subsequently: ["later", "after that", "then"],
  ultimately: ["in the end", "finally", "at bottom"],
  overall: ["on the whole", "broadly", "all told"],
  notably: ["in particular", "especially", "above all"],
  arguably: ["probably", "you could say", "likely"],
  essentially: ["basically", "in short", "at heart"],
  fundamentally: ["basically", "at heart", "at bottom"],
  // verbs
  utilize: ["use", "put to work", "rely on"],
  utilise: ["use", "put to work", "rely on"],
  leverage: ["use", "draw on", "put to work"],
  facilitate: ["help", "make easier", "support"],
  demonstrate: ["show", "prove", "make clear"],
  illustrate: ["show", "spell out", "make clear"],
  indicate: ["show", "point to", "suggest"],
  highlight: ["point out", "stress", "bring up"],
  underscore: ["stress", "drive home", "point up"],
  emphasize: ["stress", "insist on", "point up"],
  foster: ["build", "encourage", "grow"],
  cultivate: ["build", "grow", "develop"],
  navigate: ["handle", "deal with", "work through"],
  streamline: ["simplify", "tighten", "trim"],
  optimize: ["tune", "improve", "sharpen"],
  enhance: ["improve", "sharpen", "lift"],
  elevate: ["raise", "lift", "push up"],
  embark: ["start", "set out", "begin"],
  delve: ["dig", "look closely", "go deep"],
  encompass: ["cover", "include", "take in"],
  entail: ["involve", "mean", "require"],
  obtain: ["get", "pick up", "secure"],
  require: ["need", "call for", "take"],
  provide: ["give", "offer", "hand over"],
  ensure: ["make sure", "see to it", "guarantee"],
  commence: ["start", "begin", "kick off"],
  terminate: ["end", "stop", "close"],
  ascertain: ["find out", "check", "confirm"],
  // adjectives & nouns
  robust: ["solid", "sturdy", "tough"],
  seamless: ["smooth", "clean", "easy"],
  comprehensive: ["complete", "full", "thorough"],
  intricate: ["complex", "detailed", "fiddly"],
  nuanced: ["subtle", "fine-grained", "careful"],
  pivotal: ["central", "decisive", "key"],
  crucial: ["key", "make-or-break", "central"],
  vital: ["needed", "core", "central"],
  essential: ["needed", "core", "must-have"],
  significant: ["notable", "sizeable", "real"],
  substantial: ["large", "sizeable", "big"],
  myriad: ["many", "countless", "plenty of"],
  plethora: ["plenty", "a lot", "no end"],
  realm: ["field", "area", "world"],
  landscape: ["field", "scene", "picture"],
  tapestry: ["mix", "weave", "blend"],
  testament: ["proof", "sign", "evidence"],
  cornerstone: ["basis", "foundation", "core"],
  paradigm: ["model", "pattern", "framework"],
  framework: ["structure", "setup", "system"],
  insights: ["findings", "takeaways", "lessons"],
  journey: ["path", "process", "run"],
  synergy: ["fit", "combined effect", "teamwork"],
  holistic: ["whole", "all-round", "joined-up"],
  innovative: ["new", "fresh", "original"],
  cutting: ["leading", "front", "advanced"],
  meticulous: ["careful", "painstaking", "precise"],
  profound: ["deep", "far-reaching", "serious"],
};

const RAW_FR = {
  cependant: ["mais", "pourtant", "seulement"],
  néanmoins: ["pourtant", "malgré tout", "quand même"],
  toutefois: ["mais", "pourtant", "cela dit"],
  ainsi: ["donc", "du coup", "de ce fait"],
  "par conséquent": ["donc", "du coup", "résultat"],
  "de plus": ["aussi", "en plus", "par ailleurs"],
  "en outre": ["aussi", "en plus", "par ailleurs"],
  également: ["aussi", "de même", "pareillement"],
  effectivement: ["en effet", "c'est vrai", "de fait"],
  notamment: ["en particulier", "surtout", "entre autres"],
  finalement: ["au final", "pour finir", "en bout de course"],
  globalement: ["dans l'ensemble", "en gros", "pour l'essentiel"],
  véritable: ["authentique", "vrai", "réel"],
  utiliser: ["employer", "se servir de", "mobiliser"],
  faciliter: ["aider", "simplifier", "alléger"],
  démontrer: ["montrer", "prouver", "établir"],
  illustrer: ["montrer", "donner à voir", "expliciter"],
  souligner: ["insister sur", "mettre en avant", "rappeler"],
  favoriser: ["encourager", "aider", "pousser"],
  optimiser: ["améliorer", "affiner", "régler"],
  renforcer: ["consolider", "affermir", "appuyer"],
  permettre: ["laisser", "donner les moyens de", "autoriser"],
  nécessiter: ["demander", "exiger", "réclamer"],
  fournir: ["donner", "livrer", "apporter"],
  garantir: ["assurer", "promettre", "sécuriser"],
  robuste: ["solide", "fiable", "durable"],
  fluide: ["souple", "limpide", "sans heurt"],
  complet: ["exhaustif", "entier", "intégral"],
  crucial: ["clé", "décisif", "majeur"],
  essentiel: ["clé", "vital", "central"],
  incontournable: ["indispensable", "obligatoire", "majeur"],
  pertinent: ["juste", "adapté", "utile"],
  paysage: ["univers", "terrain", "décor"],
  écosystème: ["milieu", "ensemble", "réseau"],
  approche: ["méthode", "façon de faire", "optique"],
  démarche: ["méthode", "façon de faire", "manière de faire"],
  enjeux: ["questions", "défis", "points en jeu"],
  levier: ["moyen", "outil", "ressort"],
  synergie: ["complémentarité", "entente", "coopération"],
  holistique: ["d'ensemble", "global", "complet"],
  innovant: ["nouveau", "original", "inédit"],
  minutieux: ["soigné", "précis", "méticuleux"],
  profond: ["fort", "durable", "marqué"],
  // feminine forms (French adjectives are inflected, so they need their own keys)
  cruciale: ["décisive", "majeure", "centrale"],
  essentielle: ["vitale", "centrale", "primordiale"],
  pertinente: ["juste", "adaptée", "utile"],
  innovante: ["nouvelle", "originale", "inédite"],
  profonde: ["forte", "durable", "marquée"],
  complète: ["exhaustive", "entière", "intégrale"],
  globale: ["d'ensemble", "générale", "large"],
};

// Multi-word turns of phrase, applied before single tokens.
// An alternative must never be a favoured token itself, otherwise the rewrite
// would simply move the signal around.
function sanitizeDict(raw) {
  const keys = new Set(Object.keys(raw).map((k) => k.toLowerCase()));
  const out = {};
  for (const [k, alts] of Object.entries(raw)) {
    const kept = alts.filter((a) => !a.toLowerCase().split(/[\s']+/).some((w) => keys.has(w)));
    out[k] = kept.length ? kept : alts;
  }
  return out;
}

const FAVOURED_EN = sanitizeDict(RAW_EN);
const FAVOURED_FR = sanitizeDict(RAW_FR);

const PHRASES = [
  [/\bin order to\b/gi, ["to", "so as to"]],
  [/\bdue to the fact that\b/gi, ["because", "since"]],
  [/\bit is important to note that\b/gi, ["note that", "keep in mind:"]],
  [/\bit'?s important to note that\b/gi, ["note that", "keep in mind:"]],
  [/\bit is worth noting that\b/gi, ["note that", "worth saying:"]],
  [/\bwhen it comes to\b/gi, ["for", "with"]],
  [/\ba wide range of\b/gi, ["many", "all sorts of"]],
  [/\ba myriad of\b/gi, ["many", "countless"]],
  [/\bthe fact that\b/gi, ["that", "how"]],
  [/\bplays? an? (?:crucial|vital|key|pivotal|important) role in\b/gi, ["matters for", "drives"]],
  [/\bserves? as a\b/gi, ["is a", "works as a"]],
  [/\bin today'?s (?:fast-paced |modern |digital )?world,?\s*/gi, ["", "today, "]],
  [/\bin the realm of\b/gi, ["in", "within"]],
  [/\bnot only (.+?) but also\b/gi, ["$1, and also"]],
  [/\bdelve into\b/gi, ["dig into", "look closely at"]],
  [/\bcutting[- ]edge\b/gi, ["advanced", "leading"]],
  [/\bstate[- ]of[- ]the[- ]art\b/gi, ["top-tier", "advanced"]],
  [/\bgame[- ]changer\b/gi, ["turning point", "big shift"]],
  [/\bin conclusion,?\s*/gi, ["to sum up, ", "so: "]],
  [/\bà l'heure actuelle,?\s*/gi, ["aujourd'hui, ", ""]],
  [/\bil est important de noter que\b/gi, ["notons que", "à retenir :"]],
  [/\bil convient de (?:noter|souligner) que\b/gi, ["notons que", "à retenir :"]],
  [/\bdans le monde (?:actuel|d'aujourd'hui),?\s*/gi, ["aujourd'hui, ", ""]],
  [/\bjoue un rôle (?:crucial|essentiel|clé|central) dans\b/gi, ["compte pour", "pèse sur"]],
  [/\bune multitude de\b/gi, ["beaucoup de", "plein de"]],
  [/\bafin de\b/gi, ["pour", "en vue de"]],
  [/\ben raison de\b/gi, ["à cause de", "vu"]],
  [/\bnon seulement (.+?) mais (?:aussi|également)\b/gi, ["$1, et aussi"]],
  [/\bau final,?\s*/gi, ["bref, ", "résultat : "]],
];

const PUNCT = [
  [/\u2014/g, " - "], [/\u2013/g, "-"], [/\u2026/g, "..."],
  [/[\u201c\u201d]/g, '"'], [/[\u2018\u2019]/g, "'"],
  [/ +([,;:.!?])/g, "$1"], [/([.!?]) {2,}/g, "$1 "],
];

const CONTRACTIONS = [
  [/\bdo not\b/gi, "don't"], [/\bdoes not\b/gi, "doesn't"], [/\bcannot\b/gi, "can't"],
  [/\bit is\b/gi, "it's"], [/\bthat is\b/gi, "that's"], [/\bthere is\b/gi, "there's"],
  [/\bwill not\b/gi, "won't"], [/\bis not\b/gi, "isn't"], [/\bare not\b/gi, "aren't"],
];

const VOWEL = "aeiouyàâäéèêëîïôöûüh";
const FR_ELISION_FIX = [
  [new RegExp(`\\b([Dd])e ([${VOWEL}])`, "g"), "$1'$2"],
  [new RegExp(`\\b([Qq])ue ([${VOWEL}])`, "g"), "$1u'$2"],
  [new RegExp(`\\b([Ll])e ([${VOWEL}])`, "g"), "$1'$2"],
  [new RegExp(`\\b([Ll])a ([${VOWEL}])`, "g"), "$1'$2"],
  [new RegExp(`\\b([Cc])e ([${VOWEL}])`, "g"), "$1et $2"],
  [new RegExp(`\\b([Nn])e ([${VOWEL}])`, "g"), "$1'$2"],
];
const FR_STOPWORDS = /\b(le|la|les|des|une|est|et|dans|pour|qui|que|avec|nous|cette|ces)\b/gi;
const looksFrench = (s) => ((s.match(FR_STOPWORDS) || []).length / Math.max(1, wordCount(s))) > 0.06;

const ALL_FAVOURED = { ...FAVOURED_EN, ...FAVOURED_FR };
const sortKeys = (o) => Object.keys(o).sort((a, b) => b.length - a.length);
const KEYS_EN = sortKeys(FAVOURED_EN);
const KEYS_FR = sortKeys(FAVOURED_FR);
const FAVOURED_KEYS = Object.keys(ALL_FAVOURED).sort((a, b) => b.length - a.length);

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
// Only bare forms and simple plurals are matched: verb inflections (-ing, -ed,
// -ment) are left alone rather than risking a broken sentence.
const keyRe = (k) => new RegExp(`(?<!\\p{L})(${escapeRe(k)})(es|s)?(?!\\p{L})`, "giu");
// Single-pass matcher: longest keys first, so one occurrence is rewritten once
// and a replacement can never be re-replaced by a later key.
const masterRe = (keys) => new RegExp(`(?<!\\p{L})(${keys.map(escapeRe).join("|")})(es|s)?(?!\\p{L})`, "giu");
const ELISION = ["d'", "l'", "n'", "qu'", "j'", "s'"];
const startsWithVowel = (w) => /^[aeiouyàâéèêëîïôöûü]/i.test(w);
// English article choice is about sound, not spelling.
const AN_H = /^(hour|honest|honour|honor|heir)/i;
const A_VOWEL = /^(uni|use|user|usual|utili|eu|one|once)/i;
const needsAn = (w) => (AN_H.test(w) ? true : /^[aeiou]/i.test(w) && !A_VOWEL.test(w));

const matchCase = (source, target) => {
  if (!target) return target;
  if (source === source.toUpperCase() && source.length > 2) return target.toUpperCase();
  if (source[0] === source[0].toUpperCase()) return target[0].toUpperCase() + target.slice(1);
  return target;
};

const hash = (str, salt) => {
  let h = salt | 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
};

const wordCount = (s) => (s.match(/[\p{L}\p{N}']+/gu) || []).length;

/** Favoured-token density per 1000 words: the signal a sampling detector feeds on. */
export function favouredScore(text, keys = FAVOURED_KEYS) {
  const words = wordCount(text) || 1;
  let hits = 0;
  const found = {};
  text.replace(masterRe(keys), (m, base) => {
    hits += 1;
    const k = base.toLowerCase();
    found[k] = (found[k] || 0) + 1;
    return m;
  });
  for (const [re] of PHRASES) {
    const m = text.match(re);
    if (m) hits += m.length;
  }
  return { hits, words, per1000: (hits / words) * 1000, found };
}

export function disruptStatistical(text, { intensity = "balanced" } = {}) {
  const rate = intensity === "light" ? 0.5 : intensity === "strong" ? 1 : 0.85;
  const french = looksFrench(text);
  // Dictionaries are language-scoped: French alternatives must never surface in
  // English output (both dictionaries share keys such as "crucial").
  const dict = french ? FAVOURED_FR : FAVOURED_EN;
  const keys = french ? KEYS_FR : KEYS_EN;
  const scoreBefore = favouredScore(text, keys);
  let out = text;
  let rewritten = 0;
  const applied = [];

  // 1. Phrases (the strongest signal carriers)
  for (const [re, alts] of PHRASES) {
    out = out.replace(re, (m, ...rest) => {
      const groups = rest.slice(0, -2);
      const pick = alts[hash(m, 7) % alts.length];
      if (alts.length > 1 && hash(m, 13) % 100 >= rate * 100) return m;
      rewritten += 1;
      let res = pick;
      groups.forEach((g, i) => { if (g !== undefined) res = res.replace(`$${i + 1}`, g); });
      applied.push(`${m.trim().slice(0, 34)} → ${res.trim().slice(0, 34) || "(removed)"}`);
      return matchCase(m, res);
    });
  }

  // 2. Single favoured tokens, with a rotating alternative so the replacement
  //    itself does not become a new statistical fingerprint.
  const tally = {};
  const replacedStarts = new Set();
  const used = new Set();
  let delta = 0;
  out = out.replace(masterRe(keys), (m, base, suffix, offset, full) => {
    const alts = dict[base.toLowerCase()];
    if (!alts) return m;
    if (hash(m + offset, 3) % 100 >= rate * 100) return m;
    // French: keep the gender/number shape of the original word.
    const pool = french && /e$/i.test(base) ? alts.filter((a) => /e$/i.test(a)) : alts;
    if (!pool.length) return m;
    // Rotate to an alternative not used yet, so two different favoured words
    // never collapse onto the same replacement.
    let idx = hash(m + offset, 11) % pool.length;
    for (let i = 0; i < pool.length; i++) {
      const cand = pool[(idx + i) % pool.length];
      if (!used.has(cand.toLowerCase())) { idx = (idx + i) % pool.length; break; }
    }
    let alt = pool[idx];
    if (!alt) return m;
    if (suffix) {
      if (alt.includes(" ")) return m; // cannot pluralise a phrase safely
      alt = alt.endsWith("s") ? alt : `${alt}s`;
    }
    const prev = full.slice(Math.max(0, offset - 2), offset).toLowerCase();
    if (ELISION.includes(prev) && !startsWithVowel(alt)) return m;
    const k = base.toLowerCase();
    tally[k] = (tally[k] || 0) + 1;
    used.add(pool[idx].toLowerCase());
    rewritten += 1;
    const result = matchCase(m, alt);
    replacedStarts.add(offset + delta);
    delta += result.length - m.length;
    return result;
  });
  for (const [k, n] of Object.entries(tally)) applied.push(`${k} ×${n}`);

  // 2b. Repair the article ONLY in front of a word we just substituted.
  if (!french && replacedStarts.size) {
    const src = out;
    out = out.replace(/\b(an|a|An|A) (?=[\p{L}'])/gu, (m, art, offset) => {
      const wordStart = offset + m.length;
      if (!replacedStarts.has(wordStart)) return m;
      const word = (src.slice(wordStart).match(/^[\p{L}'-]+/u) || [""])[0];
      const cap = art[0] === "A";
      return `${needsAn(word) ? (cap ? "An" : "an") : cap ? "A" : "a"} `;
    });
  }

  // 3. Surface noise: punctuation and, at strong intensity, contractions
  let punctChanged = 0;
  for (const [re, rep] of PUNCT) {
    const before = out;
    out = out.replace(re, rep);
    if (out !== before) punctChanged += 1;
  }
  if (intensity === "strong") {
    for (const [re, rep] of CONTRACTIONS) {
      out = out.replace(re, (m) => { rewritten += 1; return matchCase(m, rep); });
    }
    // Split the very long sentences LLMs favour.
    out = out.replace(/([a-z]{3,}), (?:and|which) ([a-z]{3,})/g, (m, a, b) => `${a}. ${b.charAt(0).toUpperCase()}${b.slice(1)}`);
  }

  // 4. Language repair after substitutions
  if (french) {
    for (const [re, rep] of FR_ELISION_FIX) out = out.replace(re, rep);
  }

  const scoreAfter = favouredScore(out, keys);
  const reduction = scoreBefore.hits ? 1 - scoreAfter.hits / scoreBefore.hits : 0;

  return {
    text: out,
    stats: {
      tokensChanged: rewritten,
      punctChanged,
      words: scoreBefore.words,
      ratio: Math.min(1, rewritten / (scoreBefore.words || 1)),
      before: scoreBefore,
      after: scoreAfter,
      reduction,
      applied: applied.slice(0, 14),
    },
  };
}
